import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const nav = navRef.current;
      if (!nav) return;
      if (window.scrollY > 60) {
        nav.classList.add("bg-background-50/95", "backdrop-blur-md", "border-b", "border-background-200/60");
        nav.classList.remove("bg-background-50/70");
      } else {
        nav.classList.remove("bg-background-50/95", "backdrop-blur-md", "border-b", "border-background-200/60");
        nav.classList.add("bg-background-50/70");
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const groupedCategories = [
    { group: "Apparel", items: categories.slice(0, 7) },
    { group: "Accessories & More", items: categories.slice(7) },
  ];

  return (
    <nav
      id="main-nav"
      ref={navRef}
      className="fixed top-0 left-0 w-full z-50 transition-all duration-300 bg-background-50/70 backdrop-blur-md"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 cursor-pointer shrink-0">
          <span className="font-heading text-2xl font-bold text-foreground-900">itaobuy</span>
          <span className="font-label text-xs text-foreground-500 tracking-widest uppercase mt-1">.quest</span>
        </Link>

        <div className="hidden md:flex items-center gap-1.5">
          <Link to="/" className="px-4 py-2 text-sm font-medium text-foreground-800 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">Home</Link>
          <span className="text-foreground-300 text-[10px]">◆</span>
          <Link to="/itaobuy-sheet-guide" className="px-4 py-2 text-sm font-medium text-foreground-800 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">Guide</Link>
          <span className="text-foreground-300 text-[10px]">◆</span>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-4 py-2 text-sm font-medium text-accent-600 whitespace-nowrap cursor-pointer hover:text-accent-700 transition-colors"
          >
            Shopping Guide
          </a>
          <span className="text-foreground-300 text-[10px]">◆</span>
          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="px-4 py-2 text-sm font-medium text-foreground-800 hover:text-foreground-950 transition-colors whitespace-nowrap flex items-center gap-1 cursor-pointer"
            >
              Categories
              <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
            </button>
            {dropdownOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-background-50 border border-background-200/70 rounded-2xl shadow-lg p-6 w-[720px] z-50">
                {groupedCategories.map((group) => (
                  <div key={group.group} className="mb-5 last:mb-0">
                    <p className="text-[11px] font-semibold text-foreground-500 uppercase tracking-widest mb-3">{group.group}</p>
                    <div className="grid grid-cols-7 gap-2.5">
                      {group.items.map((cat) => (
                        <a
                          key={cat.name}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-background-100 transition-colors cursor-pointer group"
                        >
                          <div className="w-12 h-12 rounded-xl overflow-hidden bg-background-100">
                            <img src={cat.image} alt={cat.name} className="w-full h-full object-cover object-top" />
                          </div>
                          <span className="text-[11px] text-center leading-tight text-foreground-700 group-hover:text-foreground-900 transition-colors">{cat.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-foreground-900 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            buy it now
            <i className="ri-arrow-right-up-line text-sm"></i>
          </a>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-800 cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 px-6 py-5 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
          <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer">
            <i className="ri-home-4-line text-lg text-foreground-500"></i>
            Home
          </Link>
          <Link to="/itaobuy-sheet-guide" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer">
            <i className="ri-book-open-line text-lg text-foreground-500"></i>
            Complete Guide
          </Link>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-accent-600 cursor-pointer hover:bg-background-100 transition-colors"
          >
            <i className="ri-shopping-bag-3-line text-lg text-accent-500"></i>
            Shopping Guide
          </a>

          <button
            onClick={() => setMobileCatOpen(!mobileCatOpen)}
            className="flex items-center justify-between px-3 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer mt-2"
          >
            <span className="flex items-center gap-3">
              <i className="ri-grid-line text-lg text-foreground-500"></i>
              Categories
            </span>
            <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? "rotate-180" : ""}`}></i>
          </button>

          {mobileCatOpen && (
            <div className="px-3 pb-3 space-y-4">
              {groupedCategories.map((group) => (
                <div key={group.group}>
                  <p className="text-[11px] font-semibold text-foreground-500 uppercase tracking-widest mb-2 px-1">{group.group}</p>
                  <div className="grid grid-cols-4 gap-2">
                    {group.items.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex flex-col items-center gap-1 p-2 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-background-100">
                          <img src={cat.image} alt={cat.name} className="w-full h-full object-cover object-top" />
                        </div>
                        <span className="text-[10px] text-center leading-tight text-foreground-600">{cat.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-background-200/60">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full bg-foreground-900 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer"
            >
              buy it now
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}